import IndicatorButton from 'components/indicator/IndicatorButton';
import { DivConst } from 'constants/divConst';
import GoogleAnalyticsHelper from 'helpers/GoogleAnalyticsHelper';
import { AppHelper } from 'helpers/appHelper';
import { useApiState } from 'hooks/useApiState';
import usePageTracking from 'hooks/usePageTracking';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { QuizContentState } from 'stores/atoms/quizContentState';
import { QuizRespondentState } from 'stores/atoms/quizRespondentState';
import { QuizPublishQuestionType } from 'types/quizPublishType';

/**
 *　メールアドレス入力画面
 */
export default function QuizQuestion(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { quizCd, questionNumber } = useParams<{ quizCd: string; questionNumber: string }>();
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();

  const [quizContentState] = useRecoilState(QuizContentState);
  const [quizRespondentState, setQuizRespondentState] = useRecoilState(QuizRespondentState);

  const { postQuizAnswer } = useApiState();

  const [processingAnswer, setProcessingAnswer] = useState(false);
  const [processingNext, setProcessingNext] = useState(false);
  const [isAnswerCorrect, setIsAnswerCorrect] = useState(false);
  const [isAnswerViewState, setIsAnswerViewState] = useState(false);

  const [questionState, setQuestionState] = useState<QuizPublishQuestionType>(null);
  const [index, setIndex] = useState(0);
  const availableRespondentFields = (quizContentState?.quiz?.respondentFields || [])
    .filter(field => field.getFlag)
    .sort((a, b) => (a.no || 0) - (b.no || 0));

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    const respondentMailDiv = quizContentState.quiz?.respondentMailDiv;
    if (
      respondentMailDiv &&
      respondentMailDiv === DivConst.items.quizRespondentMail.beginning.value
    ) {
      setIndex(availableRespondentFields.length + Number(questionNumber));
    } else {
      setIndex(Number(questionNumber));
    }

    // 質問情報の取得
    const question = quizContentState.quiz?.questions?.find(v => v.no === Number(questionNumber));
    if (question) {
      setQuestionState(question);
    } else {
      navigate(`/quiz/${quizCd}`);
      return;
    }

    // GA4に送信
    if (!quizRespondentState.preview) {
      GoogleAnalyticsHelper.event('conversion', 'branq_question', 'クイズ回答開始');
    }
  }, [questionNumber]);
  // GTMトラッキング
  usePageTracking(quizContentState?.quiz?.gtmId, location.pathname, quizRespondentState?.preview);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　回答ボタン押下時の処理
   */
  const onAnswer = (answerOptionNo: number) =>
    AppHelper.submit(processingAnswer, setProcessingAnswer, async () => {
      try {
        // ユーザーが選んだ選択肢の取得
        const answerOption = questionState.options.find(v => v.no === answerOptionNo);
        // 正解の選択肢の取得
        const correctOption = questionState.options.find(v => v.correctFlag);

        // 戻るボタンで再回答している場合はトップに戻す
        for (const v of quizRespondentState.answers) {
          if (v.no === Number(questionNumber)) {
            navigate(`/quiz/${quizCd}/`);
            return;
          }
        }

        // 回答者情報の更新
        setQuizRespondentState({
          ...quizRespondentState,
          answers: [
            ...quizRespondentState.answers,
            {
              no: questionState.no,
              title: questionState.title,
              answerOptionNo: answerOption.no,
              answerOptionTitle: answerOption.title,
              correctOptionNo: correctOption.no,
              correctOptionTitle: correctOption.title,
              reportMailDescriptionHtml: questionState.reportMailDescriptionHtml,
            },
          ],
        });

        // 回答結果の表示
        setIsAnswerCorrect(answerOption.correctFlag);
        setIsAnswerViewState(true);
        navigate(`/quiz/${quizCd}/question/${questionNumber}/answer`);
      } catch (e) {
        showBoundary(e);
      }
    });

  /**
   *　次の質問ボタン押下時の処理
   */
  const onNext = () =>
    AppHelper.submit(processingNext, setProcessingNext, async () => {
      try {
        // 回答数
        const answerCount = quizRespondentState.answers.length;

        // 次の設問がある場合
        if (answerCount < quizContentState.quiz.questions.length) {
          // 次の設問情報の取得
          const questionNo = answerCount + 1;
          const question = quizContentState.quiz?.questions.find(v => v.no === questionNo);
          // 画面情報の更新
          setQuestionState(question);
          setIsAnswerCorrect(false);
          setIsAnswerViewState(false);
          navigate(`/quiz/${quizCd}/question/${Number(questionNumber) + 1}`);
        }
        // 設問の回答がすべて完了した場合
        else {
          // メールアドレス入力タイミングが「クイズ回答結果前」の場合はメールアドレス入力結果画面に遷移
          if (
            quizContentState.quiz.respondentMailDiv ===
            DivConst.items.quizRespondentMail.afterAnswer.value
          ) {
            // 結果画面に遷移する
            setQuizRespondentState({
              ...quizRespondentState,
              page: 'mail',
            });
            navigate(`/quiz/${quizCd}/form/1`);
          }
          // 上記以外はクイズ回答APIを呼び出し、結果画面に遷移
          else {
            const response = await postQuizAnswer({
              quizId: quizContentState.quiz.id,
              quizTitle: quizContentState.quiz.title,
              answers: quizRespondentState.answers,
              respondentInfo: {
                mailAddress: quizRespondentState.respondentForm?.mailAddress,
                name: quizRespondentState.respondentForm?.name,
                gender: quizRespondentState.respondentForm?.gender,
                birthDate: quizRespondentState.respondentForm?.birthDate,
                phoneNumber: quizRespondentState.respondentForm?.phoneNumber,
                residence: quizRespondentState.respondentForm?.residence,
                customField01: quizRespondentState.respondentForm?.customField01,
                customField02: quizRespondentState.respondentForm?.customField02,
                customField03: quizRespondentState.respondentForm?.customField03,
              },
              isPreview: quizRespondentState.preview,
            });
            // 結果画面に遷移する
            setQuizRespondentState({
              ...quizRespondentState,
              page: 'result',
            });
            navigate(`/quiz/${quizCd}/result/?cd=${response.quizHistoryCd}`);
          }
        }
      } catch (e) {
        showBoundary(e);
      }
    });

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {questionState && (
        <main>
          <section className="section-common-quiz">
            <div className="container">
              <div className="card card-quiz">
                <div className="card-body card-body-lg">
                  {/* プログレスバー */}
                  <div className="progress progress-quiz">
                    <div
                      style={{
                        width: `${AppHelper.formatRateToPercent(
                          index,
                          quizContentState.quiz.questions.length + availableRespondentFields.length
                        )}%`,
                      }}
                      className="progress-bar"
                      role="progressbar"
                      aria-valuenow={0}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  </div>
                  {/* 設問表示 */}
                  {questionState && !isAnswerViewState && (
                    <>
                      {/* 設問内容 */}
                      <div className="d-flex d-flex-quiz">
                        <div className="flex-shrink-0 flex-left">Q{index}</div>
                        <div className="flex-grow-1 flex-right">{questionState.title}</div>
                      </div>
                      {/* 設問画像 */}
                      {questionState.questionImageKey && (
                        <img
                          src={AppHelper.getImageUrl(questionState.questionImageKey)}
                          className="card-img img-fluid mx-auto"
                        />
                      )}
                      {/* 選択肢 */}
                      <ul className="list-unstyled list-button">
                        {questionState.options.map(option => {
                          return (
                            <li key={`quiz-question-option-${option.no}`}>
                              <button
                                onClick={() => onAnswer(option.no)}
                                className="btn btn btn-common btn-common-w238 btn-lg-sp"
                              >
                                {option.title}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}

                  {/* 回答表示 */}
                  {questionState && isAnswerViewState && (
                    <>
                      {/* 結果表示 */}
                      {isAnswerCorrect && (
                        <>
                          <h1 className="card-title">○正解</h1>
                          <p className="description">
                            {questionState.options.find(v => v.correctFlag).title}
                          </p>
                        </>
                      )}
                      {!isAnswerCorrect && (
                        <>
                          <h1 className="card-title card-title-light">×残念</h1>
                          <p className="description">
                            答え :{questionState.options.find(v => v.correctFlag).title}
                          </p>
                        </>
                      )}
                      {/* 回答用の画像 */}
                      {questionState.answerImageKey && (
                        <img
                          src={AppHelper.getImageUrl(questionState.answerImageKey)}
                          className="card-img img-fluid mx-auto mb-3"
                        />
                      )}
                      {/* 解説 */}
                      <div
                        dangerouslySetInnerHTML={{
                          __html: questionState.descriptionHtml,
                        }}
                      />
                      {/* 次へボタン */}
                      <div className="button-container">
                        <span className="ql-button">
                          <a onClick={onNext}>
                            <IndicatorButton visible={processingNext} />
                            つぎへ
                          </a>
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>
      )}
    </>
  );
}
